import { useEffect, useMemo, useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Menu,
  X,
  LayoutDashboard,
  Package,
  Wrench,
  Plus,
  Settings,
  User,
  LogOut,
  Search,
  Bell,
  ChevronDown,
  ShoppingBag,
  Cog,
  Inbox,
  MessageSquare,
  Layers,
  ClipboardList,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Logo } from '@/components/common/Logo';
import { NotificationList } from '@/components/NotificationList';
import { notificationService } from '@/services/notificationService';
import { Footer } from '@/components/common/Footer';

type VendorMode = 'products' | 'services';

interface SidebarItem {
  title: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  mode?: VendorMode;
  exact?: boolean;
}

const dashboardItem: SidebarItem = {
  title: 'Dashboard',
  href: '/vendor',
  icon: LayoutDashboard,
  exact: true,
};

const sidebarItems: SidebarItem[] = [
  // Product Mode Items
  {
    title: 'Manage Products',
    href: '/vendor/products/manage',
    icon: ShoppingBag,
    mode: 'products',
  },
  {
    title: 'Add Product',
    href: '/vendor/products/create',
    icon: Plus,
    mode: 'products',
  },
  {
    title: 'Product Requests',
    href: '/vendor/product-requests',
    icon: ClipboardList,
    mode: 'products',
  },

  // Service Mode Items
  {
    title: 'Available services',
    href: '/vendor/services/catalog',
    icon: Layers,
    mode: 'services',
  },
  {
    title: 'Manage Services',
    href: '/vendor/services/manage',
    icon: Cog,
    mode: 'services',
  },
  {
    title: 'Available Requests',
    href: '/vendor/available-requests',
    icon: Inbox,
    mode: 'services',
  },
  {
    title: 'My Assigned Requests',
    href: '/vendor/service-requests',
    icon: Wrench,
    mode: 'services',
  },
  {
    title: 'My Quotes',
    href: '/vendor/quotes',
    icon: MessageSquare,
    mode: 'services',
  },

  // Products-only Items
  { title: 'Orders', href: '/vendor/orders', icon: Package, mode: 'products' as VendorMode },

  // Common (no mode filter)
  { title: 'Support', href: '/vendor/support', icon: MessageSquare },
];

export const VendorLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [vendorMode, setVendorMode] = useState<VendorMode>('products');
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    notificationService.getUnreadCount().then((res) => {
      if (res.success) setUnreadCount(res.data?.unreadCount ?? 0);
    }).catch(() => {});
  }, []);
  const { user, logout, getUserFullName, getUserInitials, isVendorApproved } =
    useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const vendorApproved = isVendorApproved();

  const vendorType = user?.vendorType ?? 'general_vendor';
  const formatPartnerTypeLabel = (type?: string) => {
    if (!type) return 'Partner';
    switch (type) {
      case 'service_vendor':
        return 'Service Partner';
      case 'product_vendor':
        return 'Product Partner';
      case 'general_vendor':
        return 'General Partner';
      default:
        return type.replace('_', ' ');
    }
  };
  const allowedModes = useMemo(() => {
    if (vendorType === 'service_vendor') return ['services'] as const;
    if (vendorType === 'product_vendor') return ['products'] as const;
    return ['products', 'services'] as const;
  }, [vendorType]);

  // Ensure selected mode is valid for this vendorType.
  useEffect(() => {
    if (!allowedModes.includes(vendorMode)) {
      setVendorMode(allowedModes[0]);
      navigate('/vendor');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedModes, vendorMode]);

  const isActiveRoute = (href: string, exact = false) => {
    if (exact) {
      return location.pathname === href;
    }
    return location.pathname.startsWith(href);
  };

  const filteredSidebarItems = sidebarItems.filter((item) => {
    // Items with explicit mode follow the current mode.
    if (item.mode) return item.mode === vendorMode;
    // Common items are always shown.
    return true;
  });

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleModeSwitch = (mode: VendorMode) => {
    if (!allowedModes.includes(mode)) return;
    setVendorMode(mode);
    // Navigate to the main dashboard when switching modes
    navigate('/vendor');
  };

  return (
    <div className="flex h-screen bg-background">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 transform border-r border-border bg-card transition-transform duration-200 ease-in-out lg:static lg:inset-0 lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex h-16 items-center justify-between border-b border-border px-4">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <div className="flex min-w-0 flex-col">
              <span className="text-base font-semibold leading-tight text-foreground">
                Partner Portal
              </span>
            </div>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="flex-shrink-0 rounded-md p-1.5 transition-colors hover:bg-muted lg:hidden"
            aria-label="Close sidebar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Dashboard & Mode Switcher */}
        <div className="space-y-3 border-b border-border p-4">
          {/* Dashboard Link */}
          <Link
            to={dashboardItem.href}
            onClick={() => setSidebarOpen(false)}
            className={cn(
              'flex items-center justify-between rounded-lg px-3 py-2.5 transition-all',
              isActiveRoute(dashboardItem.href, dashboardItem.exact)
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'border border-border bg-muted/50 text-foreground hover:bg-muted'
            )}
          >
            <div className="flex items-center">
              <dashboardItem.icon className="mr-2.5 h-4 w-4 flex-shrink-0" />
              <span className="text-sm font-medium">{dashboardItem.title}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Package className="h-3 w-3 opacity-70" />
              <Wrench className="h-3 w-3 opacity-70" />
            </div>
          </Link>

          {/* Mode Switcher */}
          {allowedModes.length > 1 ? (
          <div>
            <div className="mb-2 px-1 text-xs font-medium text-muted-foreground">
              VIEW MODE
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => handleModeSwitch('products')}
                disabled={!allowedModes.includes('products')}
                className={cn(
                  'rounded-md p-2 text-xs font-medium transition-colors',
                  vendorMode === 'products'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                )}
              >
                <Package className="mx-auto mb-1 h-4 w-4" />
                Products
              </button>
              <button
                onClick={() => handleModeSwitch('services')}
                disabled={!allowedModes.includes('services')}
                className={cn(
                  'rounded-md p-2 text-xs font-medium transition-colors',
                  vendorMode === 'services'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                )}
              >
                <Wrench className="mx-auto mb-1 h-4 w-4" />
                Services
              </button>
            </div>
          </div>
          ) : null}
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-1 overflow-y-auto px-4 py-4">
          {filteredSidebarItems.map((item) => {
            // Disable Add Product/Add Service links if vendor is not approved
            const isDisabled =
              !vendorApproved &&
              (item.href.includes('/create') || item.href.includes('/manage'));
            const isCreateAction = item.href.includes('/create');

            if (isDisabled) {
              return (
                <div
                  key={item.href}
                  className={cn(
                    'flex cursor-not-allowed items-center rounded-md px-3 py-2 text-sm font-medium opacity-50 transition-colors',
                    'text-muted-foreground'
                  )}
                  title="Account pending approval. Please wait for admin approval."
                >
                  <item.icon className="mr-3 h-4 w-4 flex-shrink-0" />
                  {item.title}
                </div>
              );
            }

            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={cn(
                  'flex items-center rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  isActiveRoute(item.href, item.exact)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                )}
              >
                <item.icon className="mr-3 h-4 w-4 flex-shrink-0" />
                {item.title}
              </Link>
            );
          })}
        </nav>

        {/* Partner Info */}
        <div className="border-t border-border p-4">
          <div className="flex items-center space-x-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-sm font-medium text-primary-foreground">
              {getUserInitials()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-foreground">
                {getUserFullName()}
              </div>
              <div className="text-xs capitalize text-muted-foreground">
                {formatPartnerTypeLabel(user?.vendorType)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content area */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Header */}
        <header className="flex h-16 items-center justify-between border-b border-border bg-card px-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="rounded-md p-1 hover:bg-muted lg:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>

            {/* Logo */}
            <Link to="/vendor" className="hidden md:block">
              <Logo variant="full" size="sm" linkToHome={false} />
            </Link>
          </div>

          <div className="flex items-center space-x-4">
            {/* Search */}
            <div className="relative hidden md:flex">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-muted-foreground" />
              <input
                type="text"
                placeholder="Search products, services..."
                className="w-64 rounded-md border border-border bg-background py-2 pl-10 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            {/* Theme toggle */}
            <ThemeToggle size="sm" />

            {/* Notifications */}
            <div className="relative">
              <button
                onClick={() => {
                  setNotificationsOpen(!notificationsOpen);
                  setUserMenuOpen(false);
                }}
                className="relative rounded-md p-2 hover:bg-muted"
              >
                <Bell className="h-4 w-4" />
                {unreadCount > 0 && (
                  <span className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {notificationsOpen && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setNotificationsOpen(false)}
                  />
                  <div className="absolute right-0 z-50 mt-2 w-96 rounded-lg border border-border bg-card shadow-lg">
                    <div className="flex items-center justify-between border-b border-border px-4 py-3">
                      <h3 className="text-sm font-semibold">Notifications</h3>
                      <button
                        onClick={() => setNotificationsOpen(false)}
                        className="rounded p-1 hover:bg-muted"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      <NotificationList
                        limit={10}
                        showMarkAll={true}
                        onNotificationClick={() => {
                          setNotificationsOpen(false);
                        }}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* User menu */}
            <div className="relative">
              <button
                onClick={() => { setUserMenuOpen(!userMenuOpen); setNotificationsOpen(false); }}
                className="flex items-center space-x-2 rounded-md p-2 hover:bg-muted"
              >
                {user?.profile?.avatar ? (
                  <img
                    src={user.profile.avatar}
                    alt={getUserFullName()}
                    className="h-6 w-6 rounded-full border-2 border-primary/20 object-cover"
                  />
                ) : (
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground">
                    {getUserInitials()}
                  </div>
                )}
                <span className="hidden text-sm font-medium md:inline">
                  {getUserFullName()}
                </span>
                <ChevronDown className="h-3 w-3" />
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 z-50 mt-2 w-48 rounded-md border border-border bg-card shadow-lg">
                  <div className="py-1">
                    <Link
                      to="/vendor/profile"
                      className="flex items-center px-4 py-2 text-sm text-foreground hover:bg-muted"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <User className="mr-3 h-4 w-4" />
                      Profile
                    </Link>
                    <Link
                      to="/vendor/settings"
                      className="flex items-center px-4 py-2 text-sm text-foreground hover:bg-muted"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <Settings className="mr-3 h-4 w-4" />
                      Settings
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="flex w-full items-center px-4 py-2 text-sm text-foreground hover:bg-muted"
                    >
                      <LogOut className="mr-3 h-4 w-4" />
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
          <Footer supportPath="/vendor/support" />
        </main>
      </div>
    </div>
  );
};
